import React, { forwardRef } from 'react';
import { clsx } from 'clsx';
import styles from './ToggleSwitch.module.scss';

export interface ToggleSwitchProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /**
   * Additional CSS class name
   */
  className?: string;

  /**
   * Additional CSS class name for the label
   */
  labelClassName?: string;

  /**
   * Error state
   */
  error?: boolean;

  /**
   * Test ID for testing
   */
  testId?: string;

  /**
   * Label text for the toggle
   */
  label?: string;
}

export const ToggleSwitch = forwardRef<HTMLInputElement, ToggleSwitchProps>(
  (
    {
      className,
      labelClassName,
      error = false,
      disabled = false,
      testId,
      label,
      ...props
    },
    ref
  ) => {
    const toggleId = React.useId();
    const isDisabled = disabled;

    const toggleClasses = clsx(
      styles.root,
      {
        [styles.disabled]: isDisabled,
        [styles.error]: error,
        [styles.checked]: props.checked,
      },
      className
    );

    const inputClasses = clsx(styles.input);

    const switchClasses = clsx(styles.switch, {
      [styles.disabled]: isDisabled,
      [styles.error]: error,
      [styles.checked]: props.checked,
    });

    const labelClasses = clsx(styles.label, labelClassName);

    return (
      <div className={toggleClasses}>
        <div className={styles.content}>
          {label && (
            <label htmlFor={toggleId} className={labelClasses}>
              {label}
            </label>
          )}
          <div className={styles.toggleContainer}>
            <input
              ref={ref}
              type="checkbox"
              id={toggleId}
              data-testid={testId}
              className={inputClasses}
              disabled={isDisabled}
              aria-invalid={error}
              {...props}
            />
            <div className={switchClasses}>
              <div className={styles.thumb} />
            </div>
          </div>
        </div>
      </div>
    );
  }
);

ToggleSwitch.displayName = 'ToggleSwitch';
